<template>
  <tr
    ref="elementLocation"
    :class="[
      {hidden: hideUnavailable && (!isPurchasable || product.isOutOfStockAtAllWareHouses)},
      {'b2b-grid__row--match': isExactSearchMatch},
    ]"
    :data-search="isExactSearchMatch ? 'search-match' : ''"
    :qaid="`sku-grid.item-row-${rowIndex}`"
  >
    <td class="b2b-grid__cell lg:hidden xl:hidden xxl:table-cell" qaid="sku-grid.icon" />
    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.sku">
      <div class="b2b-grid__label">
        {{ getBaseProperty('sku') }}
      </div>
      <div class="b2b-grid__content" :qaid="`sku-grid.sku-${product.code}`">
        <div class="flex items-center">
          <a
            :href="`${encodedContextPath}/my-account/orders?partNumber=${product.code}`"
            class="leading-none mr-1"
            qaid="sku-grid.orderHistory"
          >
            <i class="material-icons">history</i>
          </a>
          <div>
            <div v-if="isPdp">
              <grid-text :data="{value: product.code, tooltip: getTooltipText(product, true)}" />
            </div>
            <div v-else>
              <grid-text
                :data="{
                  value: product.code,
                  tooltip: getTooltipText(product, true),
                  link: encodedContextPath + '/p/' + product.code,
                }"
              />
            </div>
            <div v-if="product.upc" qaid="sku-grid.upc" class="hidden lg:flex">
              <span class="text-gray-70">{{ product.upc }}</span>
            </div>
          </div>
        </div>
      </div>
    </td>

    <!-- Variable product variants -->
    <td
      v-for="(variant, variantKey) in product.categorization"
      :key="`variant-${variantKey}`"
      class="b2b-grid__cell lg:table-cell"
      :qaid="`sku-grid.sku-${variant.name}`"
    >
      <!-- Labels (small view only) -->
      <div class="b2b-grid__label">
        <span>{{ variant.name }}</span>
      </div>

      <div class="b2b-grid__content" :qaid="`sku-grid.sku-${variant.name}-${variant.value}`">
        <grid-text :data="{value: variant.value}" />
      </div>
    </td>

    <td class="b2b-grid__cell lg:hidden" qaid="sku-grid.upc">
      <div class="b2b-grid__label">
        {{ getBaseProperty('upc') }}
      </div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: product.upc}" />
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell h-auto lg:text-right" qaid="sku-grid.allocated">
      <div class="b2b-grid__label">
        {{ getBaseProperty('allocated') }}
      </div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: quantityAllocated}" />
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell h-auto lg:text-right" qaid="sku-grid.backordered">
      <div class="b2b-grid__label">
        {{ getBaseProperty('backordered') }}
      </div>
      <div class="b2b-grid__content b2b-grid__content--right">
        <trek-link
          v-if="quantityBackordered"
          qaid="sku-grid-backordered-link"
          underline
          button
          xsmall
          text
          @click="redirectToBackorderAllocated()"
        >
          <grid-text :data="{value: quantityBackordered}" />
        </trek-link>
      </div>
    </td>

    <!-- Variable product warehouses -->
    <template v-if="btoLeadTime">
      <td
        v-if="!isDropShipFlagEnabled"
        :colspan="product.warehouses.length"
        class="b2b-grid__cell lg:table-cell"
        qaid="sku-grid.warehouse"
      >
        <div class="b2b-grid__label">
          {{ product.warehouses && product.warehouses.length ? product.warehouses[0].id : '' }}
        </div>
        <div class="b2b-grid__content text-center" qaid="sku-grid.btoLeadTime" v-html="btoLeadTime" />
      </td>
    </template>

    <template v-else-if="productEtaAvailable">
      <td :colspan="product.warehouses.length" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.warehouse">
        <div class="b2b-grid__label">
          {{ getWarehouseTitle(product) }}
        </div>
        <div
          class="b2b-grid__content lg:text-center text-red font-medium"
          qaid="sku-grid.newProductEta"
          v-html="productEtaAvailable"
        />
      </td>
    </template>

    <template v-else-if="!isDropShipFlagEnabled">
      <td
        v-for="(warehouse, warehouseKey) in product.warehouses"
        :key="`warehouse-${warehouseKey}`"
        class="b2b-grid__cell lg:table-cell"
        qaid="sku-grid.warehouse"
      >
        <div class="b2b-grid__label">
          {{ warehouse.id }}
        </div>
        <div class="b2b-grid__content">
          <grid-warehouse
            :warehouse="warehouse"
            :is-build-to-order="product.isBTO"
            :no-eta-text="getBaseProperty('noEta')"
            :bto-text="getBaseProperty('bto')"
            :sold-out-text="getBaseProperty('soldOut')"
            :not-available-text="getBaseProperty('notAvailable')"
          />
        </div>
      </td>
    </template>

    <td v-if="!isDropShipFlagEnabled" class="b2b-grid__cell lg:table-cell h-auto" qaid="sku-grid.stock-watch">
      <div class="b2b-grid__label">
        {{ getBaseProperty('stockWatch') }}
      </div>
      <div class="b2b-grid__content">
        <grid-stock-watch
          :warehouses="product.stockWatch"
          :product-index="product.code"
          @update-stock-watch-state="(payload) => $emit('update-stock-watch-state', payload)"
        />
      </div>
    </td>

    <td v-if="displayPackageQuantity" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.package-quantity">
      <div class="b2b-grid__label">
        {{ getBaseProperty('pkgQty') }}
      </div>
      <div class="b2b-grid__content text-left lg:text-right">
        <grid-text :data="{value: product.packageQty}" />
      </div>
    </td>

    <td
      v-if="!consumerFriendlyMode"
      class="b2b-grid__cell lg:table-cell text-left lg:text-right"
      qaid="sku-grid.order-price"
    >
      <div class="b2b-grid__label">
        {{ getBaseProperty('price') }}
      </div>
      <div class="b2b-grid__content">
        <grid-price
          :data="{
            dealerCost,
            discount,
          }"
        />
      </div>
    </td>
    <td qaid="sku-grid.list" class="b2b-grid__cell lg:table-cell lg:text-right">
      <div class="b2b-grid__label">
        <i class="md-18">list</i>
      </div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: product.skuQtyInSavedList > 0 ? product.skuQtyInSavedList : ''}" />
      </div>
    </td>
    <td qaid="sku-grid.shopping_cart" class="b2b-grid__cell lg:table-cell lg:text-right">
      <div class="b2b-grid__label">
        <i class="md-18">shopping_cart</i>
      </div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: product.skuQtyInCart}" />
      </div>
    </td>
    <td class="b2b-grid__cell lg:table-cell mb-4 md:mb-0 w-16" qaid="sku-grid.qty">
      <div class="b2b-grid__label">
        {{ getBaseProperty('qty') }}
      </div>
      <div class="b2b-grid__content" :class="{'has-warning': minQtyError}">
        <numerical-input
          v-if="minQtyError"
          ref="quantityInput"
          v-tippy="{
            html: `#qty-tooltip-${productGroupIndex}-${rowIndex}`,
            theme: 'light',
            maxWidth: '20rem',
            placement: 'top',
          }"
          :data-sku="product.code"
          :disabled="!isPurchasable"
          :min="0"
          :max="9999"
          :should-clear="true"
          :sku="product.code"
        />
        <numerical-input
          v-else
          ref="quantityInput"
          :data-sku="product.code"
          :disabled="!isPurchasable"
          :min="0"
          :max="9999"
          :should-clear="true"
          :sku="product.code"
          @update-variant-qty="
            (payload) =>
              $emit('update-variant-qty', {qty: payload, sku: product.code, input: this.$refs['quantityInput']})
          "
        />
      </div>
    </td>

    <div v-show="false" :id="`qty-tooltip-${productGroupIndex}-${rowIndex}`" v-tippy-html class="hidden">
      <span v-html="minQtyErrorMessage" />
    </div>
  </tr>
</template>

<script>
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {mapGetters, mapState, mapActions} from 'vuex';
import GridText from '@/components/grid/GridText';
import GridWarehouse from '@/components/grid/GridWarehouse';
import GridStockWatch from '@/components/grid/GridStockWatch';
import GridPrice from '@/components/grid/GridPrice';
import NumericalInput from '@/components/NumericalInput';
import TrekLink from '@/components/TrekLink';

export default {
  components: {GridText, GridWarehouse, GridStockWatch, GridPrice, NumericalInput, TrekLink},

  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    hideUnavailable: Boolean,
    product: {
      type: Object,
      default: () => {},
    },
    productGroupIndex: {
      type: Number,
      default: 0,
    },
    rowIndex: {
      type: Number,
      default: 0,
    },
    productGroup: {
      type: Object,
      default: () => ({}),
    },
    minQtyError: Boolean,
    minQtyErrorMessage: {
      type: String,
      default: null,
    },
    isPdp: Boolean,
    isPlp: Boolean,
  },
  data() {
    return {
      searchResultReference: String,
    };
  },

  computed: {
    quantityBackordered() {
      const value = get(this.allocatedCounts, `${this.product.code}.quantityBackordered`, 0);
      return value !== 0 ? value : null;
    },

    quantityAllocated() {
      const value = get(this.allocatedCounts, `${this.product.code}.quantityAllocated`, 0);
      return value !== 0 ? value : null;
    },

    dealerCost() {
      return get(this.product, 'retailerPrice.wasPrice.low.formatted');
    },

    discount() {
      return get(this.product, 'retailerPrice.price.low.formatted');
    },

    isExactSearchMatch() {
      return this.requestedProductCode === this.product.code;
    },

    productEtaAvailable() {
      let newProductMessage = this.bp('newProductETA');
      let newProductETAMonthTranslation = this.bp(this.product.newProductETAMonth);
      if (this.product.newProductETAYear && newProductMessage && newProductETAMonthTranslation) {
        let newProductETADate = newProductETAMonthTranslation + ' ' + this.product.newProductETAYear;
        return newProductMessage.replace('{0}', newProductETADate);
      }
      return false;
    },

    btoLeadTime() {
      let btoLeadTime = this.bp('btoLeadTime');

      if (this.product.btoLeadTime && btoLeadTime && btoLeadTime.length) {
        return btoLeadTime.replace('{0}', this.product.btoLeadTime);
      }

      return false;
    },

    isPurchasable() {
      return !isEmpty(this.dealerCost) && !isEmpty(this.product.code) && this.product.isPurchasableB2B;
    },

    displayPackageQuantity() {
      return get(this.productGroup, 'hasPkgQty');
    },

    ...mapGetters('backend', ['consumerFriendlyMode']),
    ...mapState('backend', ['isDropShipFlagEnabled', 'encodedContextPath']),
    ...mapState('skuGrid', ['allocatedCounts']),
    ...mapState('pdp', ['requestedProductCode']),
    ...mapState('pdp', ['pdpDetailsLoaded']),
  },

  watch: {
    pdpDetailsLoaded() {
      if (this.$refs['elementLocation'].getAttribute('data-search') === 'search-match') {
        window.addEventListener('load', this.getSearchResultReference());
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.$emit('register-qty-input', this.$refs['quantityInput']);
    });
  },
  beforeDestroy() {
    window.removeEventListener('load', this.getSearchResultReference());
  },
  methods: {
    getSearchResultReference() {
      this.$nextTick(() => {
        const loadDOMDelay = 1500;
        setTimeout(() => {
          this.searchResultReference = this.$refs['elementLocation'];
          this.updateSearchResultReference(this.searchResultReference);
        }, loadDOMDelay);
      });
    },

    getTooltipText(product, onlyMFR) {
      const mfr = product.mfr ?? null;
      const upcTitle = this.getBaseProperty('upc');
      let toolTipData = {};
      if (mfr !== null) {
        toolTipData['MFR'] = mfr;
      }
      if (product.upc && !onlyMFR) {
        toolTipData['UPC'] = product.upc;
        toolTipData['upcTitle'] = upcTitle;
      }
      return toolTipData;
    },

    getWarehouseTitle(product) {
      /**This will be used to determine title of warehouse columm in lower viewports**/
      if (product.warehouses && product.warehouses.length > 0) {
        let warehousesNumber = product.warehouses.length - 1;
        let warehouseTitle = ''; //initializing empty string, to work with below concatenation
        while (warehousesNumber >= 0) {
          warehouseTitle += product.warehouses[warehousesNumber].id + ' ';
          warehousesNumber--;
        }
        return warehouseTitle;
      }
    },

    getBaseProperty(name) {
      let baseProperty = find(this.baseProperties, (baseProperty) => baseProperty.name === name);
      return baseProperty ? baseProperty.label : '';
    },
    redirectToBackorderAllocated() {
      const redirectPath = `${this.encodedContextPath}/my-account/backorders-allocated-items?productType=${this.productGroup.productType}&skuSearchTerm=${this.product.code}`;
      window.location.href = redirectPath;
    },
    ...mapActions('pdp', ['updateSearchResultReference']),
  },
};
</script>
<style scoped>
.b2b-grid .b2b-grid__row--match {
  background-color: var(--green-10);
}
</style>
